if (!customElements.get('spin-video')) {
  customElements.define(
    'spin-video',
    class SpinVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');

        if (!this.video || this.dataset.initialized === 'true') return;

        this.dataset.initialized = 'true';
        this.dragging = false;
        this.startX = 0;
        this.startTime = 0;
        this.pendingTime = null;
        this.seekInProgress = false;
        this.primed = false;
        this.priming = false;
        this.seekTimeout = null;

        this.video.muted = true;
        this.video.playsInline = true;
        this.video.setAttribute('webkit-playsinline', 'true');
        this.video.pause();

        this.addEventListener('pointerdown', this.pointerDown);
        this.addEventListener('pointermove', this.pointerMove);
        this.addEventListener('pointerup', this.pointerUp);
        this.addEventListener('pointercancel', this.pointerUp);
        this.addEventListener('keydown', this.keyDown);

        this.video.addEventListener('seeked', this.seekFinished);
        this.video.addEventListener('loadedmetadata', () => {
          this.classList.add('spin-video--ready');

          // iOS needs the video decoder warmed up before paused seeking paints reliably.
          this.primeVideo();
        });
      }

      disconnectedCallback() {
        document.documentElement.classList.remove('spin-video-is-dragging');
        clearTimeout(this.seekTimeout);
      }

      primeVideo = () => {
        if (this.primed || this.priming) return;

        this.priming = true;

        const playPromise = this.video.play();

        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              this.video.pause();
              this.video.currentTime = 0;
              this.primed = true;
              this.priming = false;
            })
            .catch(() => {
              this.priming = false;
            });
        } else {
          this.primed = true;
          this.priming = false;
        }
      };

      pointerDown = (event) => {
        if (!Number.isFinite(this.video.duration)) return;

        if (!this.primed) this.primeVideo();

        this.dragging = true;
        this.startX = event.clientX;
        this.startTime = this.video.currentTime;

        this.video.pause();
        this.setPointerCapture(event.pointerId);
        this.classList.add('spin-video--dragging');
        document.documentElement.classList.add('spin-video-is-dragging');
      };

      pointerMove = (event) => {
        if (!this.dragging || !Number.isFinite(this.video.duration)) return;

        const distance = event.clientX - this.startX;
        const timeChange =
          -(distance / this.clientWidth) * this.video.duration;

        const requestedTime = this.wrapTime(
          this.startTime + timeChange,
          this.video.duration
        );

        this.queueSeek(requestedTime);
      };

      pointerUp = (event) => {
        if (!this.dragging) return;

        this.dragging = false;
        this.classList.remove('spin-video--dragging');
        document.documentElement.classList.remove('spin-video-is-dragging');

        if (this.hasPointerCapture(event.pointerId)) {
          this.releasePointerCapture(event.pointerId);
        }
      };

      queueSeek(time) {
        this.pendingTime = time;

        if (!this.seekInProgress) {
          this.performSeek();
        }
      }

      performSeek() {
        if (this.pendingTime === null) return;

        const nextTime = this.pendingTime;
        this.pendingTime = null;
        this.seekInProgress = true;

        clearTimeout(this.seekTimeout);

        // Some iOS versions do not reliably fire "seeked"; keep the queue moving.
        this.seekTimeout = setTimeout(() => {
          this.seekFinished();
        }, 200);

        this.video.currentTime = nextTime;
      }

      seekFinished = () => {
        clearTimeout(this.seekTimeout);
        this.seekInProgress = false;

        if (this.pendingTime !== null) {
          this.performSeek();
        }
      };

      keyDown = (event) => {
        if (!Number.isFinite(this.video.duration)) return;

        const step = this.video.duration / 72;

        if (event.key === 'ArrowLeft') {
          event.preventDefault();

          this.queueSeek(
            this.wrapTime(this.video.currentTime - step, this.video.duration)
          );
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();

          this.queueSeek(
            this.wrapTime(this.video.currentTime + step, this.video.duration)
          );
        }

        if (event.key === 'Home') {
          event.preventDefault();
          this.queueSeek(0);
        }
      };

      wrapTime(time, duration) {
        return ((time % duration) + duration) % duration;
      }
    }
  );
}